import React, { cloneElement } from 'react';
import { isCollapsed, useEditorState } from '@udecode/plate-common';
import { useReadOnly, useSelected } from 'slate-react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  useInteractions,
} from '@floating-ui/react';

/**
 * Popover displayed over an element if:
 * - not disabled
 * - not read-only
 * - element selected
 */
export const ElementPopover = ({
  floatingOptions = {},
  ...props
}) => {
  const { disabled } = props;

  const readOnly = useReadOnly();
  const selected = useSelected();
  const editor = useEditorState();

  return (
    <Popover
      floatingOptions={{
        open:
          !disabled && !readOnly && selected && isCollapsed(editor.selection),
        ...floatingOptions,
      }}
      {...props}
    />
  );
};

/**
 * Popover displayed over children, rendering `content`
 */
export const Popover = ({
  floatingOptions,
  children,
  content,
  ...props
}) => {
  const { x, y, refs, strategy } = useFloating({
    middleware: [
      offset(12),
      flip({
        padding: 96,
      }),
      shift(),
    ],
    whileElementsMounted: autoUpdate,
    ...floatingOptions,
  });

  const { getReferenceProps } = useInteractions();

  return (
    <>
      {cloneElement(
        children,
        getReferenceProps({ ref: refs.setReference, ...children.props }),
      )}

      {floatingOptions?.open && (
        <div
          ref={refs.setFloating}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            zIndex: 41,
          }}
          contentEditable={false}
          children={content}
          {...props}
        />
      )}
    </>
  );
};
